import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { CookiePolicy } from '@ott/cookie-policy';
import { authActions } from '@ott/solar-auth';
import Footer from '@ott/solar-footer';
import Header, { HEADER_THEME_DARK } from '@ott/solar-header';
import sendKM from '@ott/utility-km';

import { getAuthSelector } from 'src/redux/selectors/commonSelectors';

import SupportChat from 'src/components/blocks/SupportChat';
import FadeIn from 'src/components/elements/FadeIn';
import { Tag } from 'src/components/elements/Tag';

import { COOKIE_POLICY_LINK } from 'src/constants/common';

import useIsWebview from 'src/hooks/useIsWebview';
import useTypedSelector from 'src/hooks/useTypedSelector';

import { NewsCard } from './blocks/NewsCard';
import { useNewsData } from './hooks/useNewsData';
import { useNewsQuery } from './hooks/useNewsQuery';

import { AnimatePresence, motion, MotionConfig } from 'framer-motion';

import styles from './NewsPage.scss';

const NewsPage = () => {
  const isWebview = useIsWebview();
  const history = useHistory();

  const { data, isLoading } = useNewsQuery();
  const {
    newsList,
    categoriesList,
    selectedCategory,
    setSelectedCategory,
    limit,
    resetLimit,
    loadMore,
  } = useNewsData({
    data,
  });

  const handleCategoryClick = (category: string) => {
    resetLimit();
    setSelectedCategory((prevState) => {
      if (prevState === category) {
        return undefined;
      }

      return category;
    });
  };

  const limitedNewsList = useMemo(() => {
    return newsList.slice(0, limit);
  }, [newsList]);

  const [newMessages, setNewMessages] = useState(0);
  const [isSupportChatVisible, setIsSupportChatVisible] = useState(false);
  const [isSupportChatOpen, setIsSupportChatOpen] = useState(false);

  const auth = useTypedSelector(getAuthSelector);
  const dispatch = useDispatch();
  const isAuthorized = auth?.data?.auth;

  const handleNewMessages = useCallback((count: number) => {
    setNewMessages(count);
  }, []);

  const handleOpenSupportChat = useCallback((isOpen: boolean) => {
    setIsSupportChatOpen(isOpen);
  }, []);

  const onSupportChatVisible = useCallback((isVisible: boolean) => {
    setIsSupportChatVisible(isVisible);
  }, []);

  useEffect(() => {
    sendKM('SOLAR_MEDIA_PAGE_SHOW');

    if (auth.infoStatus.initial) {
      dispatch(authActions.getBasicUserInfo());
    }
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      {!isWebview && (
        <>
          <CookiePolicy cookieLink={COOKIE_POLICY_LINK} />
          <Header
            theme={HEADER_THEME_DARK}
            history={history}
            showNavigation={true}
            isTransparent={true}
            supportNewMessagesCount={newMessages}
            onOpenSupportChat={handleOpenSupportChat}
            isSupportChatVisible={isSupportChatVisible}
          />
        </>
      )}

      <main className={styles.page}>
        <section className={styles.introSection}>
          <FadeIn>
            <h1 className={styles.introTitle}>
              News <span className={styles.titleSymbol}>&</span>&nbsp;Media
            </h1>
          </FadeIn>
        </section>

        <section className={styles.newsSection}>
          {isLoading ? (
            <ul className={styles.tagList}>
              <li className={styles.tagSkeleton} />
              <li className={styles.tagSkeleton} />
              <li className={styles.tagSkeleton} />
            </ul>
          ) : (
            <>
              {categoriesList.length > 0 && (
                <ul className={styles.tagList}>
                  <li>
                    <Tag
                      onClick={() => setSelectedCategory(undefined)}
                      isActive={selectedCategory === undefined}
                    >
                      All news
                    </Tag>
                  </li>
                  {categoriesList.map((categoryItem) => (
                    <li key={categoryItem}>
                      <Tag
                        isActive={selectedCategory === categoryItem}
                        onClick={() => handleCategoryClick(categoryItem)}
                        className={styles.tag}
                      >
                        {categoryItem}
                      </Tag>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {isLoading ? (
            <ul className={styles.newsList}>
              <li className={styles.newsCardSkeleton} />
              <li className={styles.newsCardSkeleton} />
              <li className={styles.newsCardSkeleton} />
            </ul>
          ) : (
            <ul className={styles.newsList}>
              <AnimatePresence>
                {limitedNewsList.map((newsItem, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, y: 150 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 150 }}
                    transition={{
                      duration: 0.4,
                      type: 'spring',
                    }}
                    layout
                    className={styles.newsListItem}
                  >
                    <NewsCard {...newsItem} />
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}

          {limit < newsList.length && (
            <button onClick={loadMore} className={styles.loadMoreButton}>
              Load more
            </button>
          )}
        </section>

        <section className={styles.connectSection}>
          <div className={styles.connectWrapper}>
            <h2 className={styles.title}>
              <span className={styles.titleSymbol}>Let's</span> connect
            </h2>
            <p className={styles.connectDescription}>
              Reach out with questions, collaboration ideas, or&nbsp;feedback. Shape the future
              of&nbsp;travel with&nbsp;us!
            </p>
            <a href="mailto:media@solartrip.com" className={styles.connectButton}>
              media@solartrip.com
            </a>
          </div>
        </section>
      </main>
      {!isWebview && <Footer />}
      <SupportChat
        isUserAuthorized={isAuthorized}
        isSolar={true}
        isChatButtonHidden={true}
        isOpenFromOutside={isSupportChatOpen}
        onOpenFromOutside={handleOpenSupportChat}
        onReceiveNewMessages={handleNewMessages}
        onCheckAvailability={onSupportChatVisible}
      />
    </MotionConfig>
  );
};

export default NewsPage;
